'use strict';

import * as dialog from '../components/dialog';
import * as progress from '../components/progress';

import InputFormat from './module.input-format';
import WindowScroll from './module.window-scroll';
import {publish} from '../helpers/pubSub';
import modules from '../helpers/modules';
import {getQueryStringParams} from '../helpers/urlUtilities';
import {addEventListenerList, getPageActionData, initModules} from '../helpers/utilities';
import {validateForm, setCustomErrors} from '../components/clientSideValidation';
import {createElWithAttrs, callbackOnElements, createCookie, objectAssign} from '@borngroup/born-utilities';

const STATUS_LABELS = {
    SUCCESS: 'OK',
    ERROR: 'Error'
};

/**
 * Runs the provided actions, loops if more than one.
 */
function _doneActions(formEl, actionData, actionType) {
    if (actionData instanceof Array) {
        actionData.forEach(currentData => _runAction(formEl, currentData, actionType));
    } else {
        _runAction(formEl, actionData, actionType);
    }
}

/**
 * Runs a single action from the list.
 * Provides an easy way to execute common actions after a form is submitted.
 */
function _runAction(formEl, actionData, actionType) {
    let actionName = typeof actionData === 'string' ? actionData : (actionData ? actionData.action : '');

    formEl.classList.add(`set--form-${actionType}`);

    switch (actionName) {
    case 'close': {
        dialog.close();

        return true;
    }
    case 'reload': {
        window.location.reload();

        return true;
    }
    case 'reset': {
        formEl.reset();

        return true;
    }
    case 'scrollTo': {
        WindowScroll.setupScroll(actionData.settings.target, 300, actionData.settings.offset);

        return true;
    }
    case 'deleteElement': {
        let targetElement = document.querySelector(actionData.selector);

        if (targetElement) {
            targetElement.parentNode.removeChild(targetElement);
        }

        return true;
    }
    case 'createCookie': {
        createCookie(actionData.settings.cookieName, actionData.settings.cookieValue, actionData.settings.cookieDuration);

        return true;
    }
    default: {
        break;
    }
    }
}

//Displays an inline message if a message is passed and a message target is available.
function _displayMessages(messageContainer, messageList) {
    if (messageList) {
        if (typeof messageList === 'string') {
            messageContainer.textContent = messageList;
        } else if (messageList.length) {
            messageList.forEach(function(currentMessage) {
                messageContainer.textContent = currentMessage;
            });
        }

        messageContainer.classList.add('set--visible');
    }
}

/**
 * Handles the response error flow.
 */
function _handleError(err) {
    progress.hideLoader();

    console.error(err);
}

function _handleTracking(formSettings, error) {
    if (formSettings.tracking) {
        let trackObject = objectAssign({submissionStatus: error ? STATUS_LABELS.ERROR : STATUS_LABELS.SUCCESS}, formSettings.tracking.trackObject);

        modules.tracking.init(null, {trackEvent: formSettings.tracking.trackEvent, trackObject});
    }
}

/**
 * Use `responseContent` to replace the contents of a `responseTargetEl` element.
 */
function _replaceContent(responseContent, responseTargetEl, formSettings) {
    if (responseTargetEl === formSettings.activeDialog) {
        dialog.replace({content: responseContent});
    } else {
        if (formSettings.responseTargetStrategy === 'update') {
            responseTargetEl.innerHTML = responseContent;
        } else if (formSettings.responseTargetStrategy === 'append') {
            responseTargetEl.insertAdjacentHTML('beforeend', responseContent);
        } else {
            responseTargetEl.insertAdjacentHTML('afterend', responseContent);
            responseTargetEl.parentNode.removeChild(responseTargetEl);
        }

        initModules(responseTargetEl);
    }

    if (typeof formSettings.onReplaceCallback === 'function') {
        formSettings.onReplaceCallback(responseContent, responseTargetEl);
    }
}

/**
 * Retrieves the form's settings attribute (data-ajax-form) and JSON parses it.
 * data-ajax-form can have the following attributes:
 * -submitSelector: A selector to target the submit action element. Defaults to [data-continue-action].
 * -errorsSelector: A selector to target the errors container element. Defaults to [data-ajax-errors].
 *                  If no error element is found, places the error at the end of the form.
 */
function _getFormSettings(formEl) {
    if (formEl !== null) {
        let formAjaxAttrVal = formEl.getAttribute('data-ajax-form');

        formEl.removeAttribute('data-ajax-form');
        return formAjaxAttrVal ? JSON.parse(formAjaxAttrVal) : {};
    }
    return;
}

/**
 * Append the page's data-querystring and data-action attributes to the request `formData` object.
 * This can be useful for certain Controllers that leverage this information for processing or redirection purposes.
 */
function _appendExternalData(formSettings, formData) {
    let pageData = getPageActionData();

    if (pageData) {
        if (formSettings.appendPageQueryString) {
            formData.queryString = pageData.queryString;
        }

        if (formSettings.appendPageAction) {
            formData.action = pageData.action;
        }
    }
}

/**
 * Appends data stored in the `submittedAction` into the request `formData` obeject.
 * This can be useful to construct a form with mutliple "submit" CTAs each with a different set of custom data that should be submitted.
 */
function _appendSubmittedActionData(submittedAction, formData) {
    if (submittedAction && submittedAction.hasAttribute('data-ajax-form-data')) {
        objectAssign(formData, JSON.parse(submittedAction.getAttribute('data-ajax-form-data')));
    }
}

/**
 * Manages necessary handling for a successful form submission,
 * including message display and success action triggering.
 */
function _handleFormSubmitSuccess(formEl, formData, formSettings, response) {
    publish(`${formSettings.formTriggerName}:success`, {formEl, formData, response});

    _doneActions(formEl, formSettings.onSuccess, 'success');

    formSettings.errorsContainerEl.classList.remove('set--visible');

    _displayMessages(formSettings.successContainerEl, response.msg || response.message);

    _handleTracking(formSettings);

    return response;
}

/**
 * Manages necessary handling for a failed form submission,
 * including error message display and success action triggering.
 */
function _handleFormSubmitError(formEl, formData, formSettings, response) {
    progress.hideLoader();

    publish(`${formSettings.formTriggerName}:error`, {formEl, formData, response});

    _doneActions(formEl, formSettings.onError, 'error');

    formSettings.successContainerEl.classList.remove('set--visible');

    _displayMessages(formSettings.errorsContainerEl, response.msg || response.message || response.error || response.serverErrors);

    setCustomErrors(formEl, response.fields || response.fieldErrors);

    _handleTracking(formSettings, true);

    return response;
}

//Sets attributes for datalayer from the response
function _setTrackingAttributesFromResponse(response, formSettings) {
    if (response.user_email && formSettings.tracking) {
        if (!formSettings.tracking.trackObject) {
            formSettings.tracking.trackObject = {};
        }
        formSettings.tracking.trackObject.user_email = response.user_email;
    }
    if (response.gtmAddAddressEvent && !formSettings.tracking) {
        formSettings.tracking = response.gtmAddAddressEvent;
    }
}

/**
 * Submits an XHR request for `formEl` using the provided `formData` and `formSettings`.
 * @param  {[type]} formEl       [description]
 * @param  {[type]} formData     [description]
 * @param  {[type]} formSettings [description]
 * @return {[type]}              [description]
 */
function _requestFormSubmit(formEl, formData, formSettings) {
    return $.ajax({
        url: formEl.action,
        type: formSettings.type || formEl.method || 'POST',
        data: formData,
        success: function(response) {
            progress.hideLoader();

            _doneActions(formEl, formSettings.onComplete, 'complete');

            if (typeof response === 'object') {
                let responseContent = response.content || response.renderedTemplate;

                if (response.success) {
                    _setTrackingAttributesFromResponse(response, formSettings);
                    _handleFormSubmitSuccess(formEl, formData, formSettings, response);
                } else {
                    _handleFormSubmitError(formEl, formData, formSettings, response);
                }

                //Replaces the content of the current modal with whatever the response is.
                //Useful to display BED errors or update messages.
                if (responseContent) {
                    _replaceContent(responseContent, formSettings.responseTargetEl, formSettings);
                }

                //Opens an additional modal that overlays the form.
                //Useful to display additional information in the form of a modal.
                if (response.openModal) {
                    dialog.open({
                        url: response.openModal,
                        type: response.dataType || null,
                        options: formSettings.doneModalOptions
                    });
                }

                if (response.tracking) {
                    formSettings.tracking = response.tracking;

                    _handleTracking(formSettings);
                }

                //Redirects to another URL.
                if (response.redirectUrl) {
                    if (window.location.href === response.redirectUrl) {
                        window.location.reload();
                    } else {
                        window.location = formSettings.appendHashOnRedirect ? (`${response.redirectUrl}${window.location.hash}`) : response.redirectUrl;
                    }
                }
            } else if (typeof response === 'string') {
                _replaceContent(response, formSettings.responseTargetEl, formSettings);
                let userEmailEl = document.querySelector('[data-locator-component="user-email"]');
                if (userEmailEl) {
                    let trackingAttributes = {};
                    trackingAttributes.user_email = userEmailEl.getAttribute('value');
                    _setTrackingAttributesFromResponse(trackingAttributes, formSettings);
                }
                if (formSettings.tracking) {
                    _handleTracking(formSettings);
                }
            }
        },
        error: _handleError
    });
}

/**
 * Preparees the provided `formEl` for submission,
 * by configuring additional properties on `formSettings` and creating a `formData` object.
 * @return {Promise}              [description]
 */
export function processAjaxForm(formEl, formSettings, evt) {
    let $form = $(formEl),
        formData = getQueryStringParams($form.serialize(), null);

    formSettings = formSettings || _getFormSettings(formEl);

    formSettings.formTriggerName = formSettings.formTriggerName || 'form';
    formSettings.submittedAction = formSettings.submittedAction || formEl.querySelector(formSettings.submitSelector || '[data-ajax-submit]');

    formSettings.errorsContainerEl = formEl.querySelector(formSettings.errorsSelector || '[data-ajax-errors]') || createElWithAttrs(formEl, {class: 'invalid-feedback', 'data-ajax-errors': true}, 'p');
    formSettings.successContainerEl = formEl.querySelector(formSettings.successSelector || '[data-ajax-success]') || createElWithAttrs(formEl, {class: 'success-feedback', 'data-ajax-success': true}, 'p');

    //Define the content replacement strategy when the response provides HTML.
    //Types: 'update', 'replace', 'append'.
    //Default: 'update'.
    formSettings.activeDialog = dialog.getActive();
    formSettings.responseTargetStrategy = formSettings.responseTargetStrategy || 'update';
    formSettings.responseTargetEl = formSettings.responseTarget ? document.querySelector(formSettings.responseTarget) : (formSettings.activeDialog || formEl);

    publish(`${formSettings.formTriggerName}:submit`, {formEl, formData, evt});

    if (validateForm(formEl)) {
        if (formSettings.submittedAction && formSettings.submittedAction.name) {
            formData[formSettings.submittedAction.name] = formSettings.submittedAction.value;
        }

        progress.showLoader(formEl);

        _appendExternalData(formSettings, formData);
        _appendSubmittedActionData(formSettings.submittedAction, formData);

        InputFormat.sanitizeFormattedData(formEl, formData);

        //Checks if Google reCaptcha is enabled and requests a user score before proceeding to submit a form.
        if (window.SFRA.SitePreferences.enableGoogleRecaptcha) {
            let formNameForRecaptchaVerificationObj = window.SFRA.SitePreferences.formNameForRecaptchaVerification ? JSON.parse(window.SFRA.SitePreferences.formNameForRecaptchaVerification) : {};

            if (formEl.name && formNameForRecaptchaVerificationObj[formEl.name]) {
                return import('../helpers/reCaptcha.js').then(reCaptcha => {
                    return reCaptcha.executeVerification().then(function(response) {
                        if (response.token) {
                            formData['gRecaptchaToken'] = response.token;
                            return _requestFormSubmit(formEl, formData, formSettings);
                        } else {
                            return _handleFormSubmitError(formEl, formData, formSettings, response);
                        }
                    });
                });
            }
        }
        backInStockTracking(formEl);
        return _requestFormSubmit(formEl, formData, formSettings);
    } else {
        _handleTracking(formSettings, true);

        publish(`${formSettings.formTriggerName}:validationError`, {formEl, formData, evt});
    }
}

/**
 * Sets up a simple AJAX form handling. Depends on the controller to provide a valid JSON response.
 * Uses the 'data-ajax-form' attribute to select which forms will use this method.
 * @param  {[type]} currentForm [description]
 */
export function setupAJAXForm(currentForm) {
    let formSettings = _getFormSettings(currentForm),
        submitSelector = formSettings.submitActionSelector || '[type="submit"]',
        submitActionEvent = formSettings.submitActionEvent || 'click';

    addEventListenerList(currentForm.querySelectorAll(submitSelector), submitActionEvent, function(evt) {
        formSettings.submittedAction = this;

        if (this.type !== 'submit') {
            //Does not work on certain browsers (FireFox) that set an `isTrusted: false` property in the event.
            //dispatchEvent(currentForm, 'submit');
            //Instead, process the form directly:
            processAjaxForm(currentForm, formSettings, evt);
        }
    });

    currentForm.addEventListener((formSettings.event || 'submit'), function(evt) {
        evt.preventDefault();

        processAjaxForm(currentForm, formSettings, evt);
    });
}

export function init(options) {
    callbackOnElements(options.elements, setupAJAXForm);
}

export function backInStockTracking(formEl) {
    var trackingParams,
        trackingParamsObj;
    trackingParams = formEl.getAttribute('data-component-onclicktrackingparams-backinstock');
    if (trackingParams) {
        trackingParamsObj = JSON.parse(trackingParams);
        modules.tracking.init(null, {
            trackEvent: trackingParamsObj.trackEvent || 'virtualPageview',
            trackObject: trackingParamsObj.trackObject
        });
    }
}
